import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Header as AriaHeader,
  ListBoxSection as AriaSection,
} from 'react-aria-components';

import { Button } from '@/lib/base/buttons/button';
import { Select } from '@/lib/base/forms/select/select';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from '@/lib/base/overlays/modal';
import { CrmFieldValueInput } from '@/lib/crm/components/fields/CrmFieldValueInput';
import { SelectValueLabel } from '@/lib/crm/components/properties/labels/SelectValueLabel';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useStages } from '@/lib/crm/hooks/useStages';
import { useStageTypeNames } from '@/lib/crm/hooks/useStageTypeNames';
import type {
  UpdatableAssociationField,
  UpdatableField,
  UpdatablePropertyField,
} from '@/lib/crm/hooks/useUpdatableFields';
import { useUpdatableFields } from '@/lib/crm/hooks/useUpdatableFields';
import { useUpdateFieldSubmit } from '@/lib/crm/hooks/useUpdateFieldSubmit';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject, EntityTypeName, FilterGroup } from '@/types/crm';

export interface BulkUpdateModalProps {
  isOpen: boolean;
  orgId: number;
  entityTypeName: EntityTypeName;
  selectedObjects: CrmObject[];
  filterGroups?: FilterGroup[];
  excludedEntityIds?: string[];
  search?: string;
  searchProperties?: string[];
  isSelectAllMode: boolean;
  totalCount: number;
  onClose: () => void;
  onAsyncDispatched: (jobId: number, totalCount: number) => void;
  onSyncComplete: () => void;
}

// Shared header styling for the grouped field dropdown (matches EntityTypeahead).
const FIELD_GROUP_HEADER_CLASS = 'px-3 py-1.5 text-caption text-tertiary';

function getEntityTypeLabel(entityTypeName: string): string {
  if (entityTypeName === 'ahoy_contact') {
    return 'contact';
  }
  if (entityTypeName === 'ahoy_deal') {
    return 'deal';
  }
  return entityTypeName.replace('ahoy_', '').replace(/_/g, ' ');
}

function getEntityTypeLabelPlural(entityTypeName: string): string {
  if (entityTypeName === 'ahoy_contact') {
    return 'contacts';
  }
  if (entityTypeName === 'ahoy_deal') {
    return 'deals';
  }
  return `${getEntityTypeLabel(entityTypeName)}s`;
}

function isValueNonEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

/**
 * Stable dropdown key for a field. `kind` is included so a property and an
 * association that happen to share a name never collide into one option.
 */
function getFieldKey(field: UpdatableField): string {
  return `${field.kind}:${field.name}`;
}

export function BulkUpdateModal({
  isOpen,
  orgId,
  entityTypeName,
  selectedObjects,
  filterGroups,
  excludedEntityIds,
  search,
  searchProperties,
  isSelectAllMode,
  totalCount,
  onClose,
  onAsyncDispatched,
  onSyncComplete,
}: BulkUpdateModalProps) {
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const [propertyValue, setPropertyValue] = useState<unknown>(null);
  const [associationValue, setAssociationValue] = useState<string[] | null>(
    null
  );
  const [associationDisplayName, setAssociationDisplayName] = useState<
    string | null
  >(null);
  const [stageValue, setStageValue] = useState<string | null>(null);

  // All hooks must be called before any conditional returns
  const { fields, isLoading: isFieldsLoading } = useUpdatableFields(
    orgId,
    entityTypeName
  );
  const { stageTypeNames, isLoading: isStageTypesLoading } =
    useStageTypeNames(orgId);
  const { getStagesByType } = useStages(orgId);
  const { associationTypes } = useAssociationTypes(orgId);

  const { submit, isSubmitting } = useUpdateFieldSubmit({
    orgId,
    entityTypeName,
    selectedObjects,
    isSelectAllMode,
    filterGroups,
    excludedEntityIds,
    search,
    searchProperties,
    onAsyncDispatched,
    onSyncComplete,
    onClose,
  });

  // Reset all field/value state on close so reopening starts fresh
  useEffect(() => {
    if (!isOpen) {
      setSelectedFieldKey(null);
      setPropertyValue(null);
      setAssociationValue(null);
      setAssociationDisplayName(null);
      setStageValue(null);
    }
  }, [isOpen]);

  const isLoading = isFieldsLoading || isStageTypesLoading;
  const displayCount = isSelectAllMode ? totalCount : selectedObjects.length;
  const entityLabel = useMemo(
    () =>
      displayCount === 1
        ? getEntityTypeLabel(entityTypeName)
        : getEntityTypeLabelPlural(entityTypeName),
    [displayCount, entityTypeName]
  );

  // Lookup from dropdown key → field, so onSelectionChange can resolve the
  // chosen field object.
  const fieldByKey = useMemo(() => {
    const map = new Map<string, UpdatableField>();
    for (const field of fields) {
      map.set(getFieldKey(field), field);
    }
    return map;
  }, [fields]);

  const selectedField = useMemo<UpdatableField | null>(
    () =>
      selectedFieldKey ? (fieldByKey.get(selectedFieldKey) ?? null) : null,
    [selectedFieldKey, fieldByKey]
  );

  // Grouped dropdown options: stage associations (pinned first), then
  // properties, then other single-value associations.
  const stageOptions = useMemo(
    () =>
      fields
        .filter(
          (f): f is UpdatableAssociationField =>
            f.kind === 'association' &&
            stageTypeNames.has(f.targetEntityTypeName)
        )
        .map(f => ({ key: getFieldKey(f), label: f.label })),
    [fields, stageTypeNames]
  );

  const propertyOptions = useMemo(
    () =>
      fields
        .filter((f): f is UpdatablePropertyField => f.kind === 'property')
        .map(f => ({ key: getFieldKey(f), label: f.label })),
    [fields]
  );

  const associationOptions = useMemo(
    () =>
      fields
        .filter(
          (f): f is UpdatableAssociationField =>
            f.kind === 'association' &&
            !stageTypeNames.has(f.targetEntityTypeName)
        )
        .map(f => ({ key: getFieldKey(f), label: f.label })),
    [fields, stageTypeNames]
  );

  // Full AssociationType object needed by AssociatedCrmObjectInput
  const selectedAssociationType = useMemo<AssociationType | null>(() => {
    if (!selectedField || selectedField.kind !== 'association') {
      return null;
    }
    return (
      associationTypes.find(
        at => at.association_type_name === selectedField.name
      ) ?? null
    );
  }, [selectedField, associationTypes]);

  // Stages list for the currently selected stage association field
  const stagesForSelectedField = useMemo(() => {
    if (!selectedField || selectedField.kind !== 'association') {
      return [];
    }
    if (!stageTypeNames.has(selectedField.targetEntityTypeName)) {
      return [];
    }
    return getStagesByType(selectedField.targetEntityTypeName);
  }, [selectedField, stageTypeNames, getStagesByType]);

  const isSelectedFieldMultiProperty = useMemo(
    () =>
      !!(
        selectedField &&
        selectedField.kind === 'property' &&
        selectedField.schema.is_multi
      ),
    [selectedField]
  );

  const isSelectedFieldStageAssoc = useMemo(
    () =>
      !!(
        selectedField &&
        selectedField.kind === 'association' &&
        stageTypeNames.has(selectedField.targetEntityTypeName)
      ),
    [selectedField, stageTypeNames]
  );

  const canConfirm = useMemo(() => {
    if (!selectedField || isSubmitting) {
      return false;
    }
    if (selectedField.kind === 'property') {
      return isValueNonEmpty(propertyValue);
    }
    if (isSelectedFieldStageAssoc) {
      return !!stageValue;
    }
    return Array.isArray(associationValue) && associationValue.length > 0;
  }, [
    selectedField,
    isSubmitting,
    propertyValue,
    stageValue,
    associationValue,
    isSelectedFieldStageAssoc,
  ]);

  // Human-readable summary of the chosen value for the confirm scope line.
  // ReactNode (not string): select/boolean values render through the
  // canonical SelectValueLabel so the confirm line shows the exact label
  // every other surface shows for the same value (e.g. Yes/No for booleans,
  // option labels for selects) instead of a hand-rolled lookup.
  const valueDisplay = useMemo<ReactNode>(() => {
    if (!selectedField) {
      return null;
    }
    if (selectedField.kind === 'property') {
      if (
        selectedField.schema.is_multi &&
        Array.isArray(propertyValue) &&
        propertyValue.length > 0
      ) {
        return `${propertyValue.length} value${propertyValue.length !== 1 ? 's' : ''}`;
      }
      if (
        propertyValue === null ||
        propertyValue === undefined ||
        propertyValue === ''
      ) {
        return null;
      }
      const fieldType = selectedField.schema.field_type_name;
      if (fieldType === 'select' || fieldType === 'boolean') {
        // The confirm line should read "Private", not the raw option key.
        // SelectValueLabel string-compares option values, so the stringified
        // value SelectInput hands back matches even a runtime-numeric option.
        return (
          <SelectValueLabel
            value={String(propertyValue)}
            propertyType={selectedField.schema}
            fontSize='sm'
          />
        );
      }
      return String(propertyValue);
    }
    if (isSelectedFieldStageAssoc) {
      const stage = stagesForSelectedField.find(
        s => s.entity_id === stageValue
      );
      // No id fallback: an unresolved stage degrades to a generic summary
      // rather than exposing the raw entity_id (matching the association
      // branch below, which degrades to empty).
      return stage?.label ?? null;
    }
    return associationDisplayName ?? null;
  }, [
    selectedField,
    propertyValue,
    stageValue,
    stagesForSelectedField,
    associationDisplayName,
    isSelectedFieldStageAssoc,
  ]);

  const handleFieldKeyChange = useCallback((key: string | number | null) => {
    setSelectedFieldKey(typeof key === 'string' ? key : null);
    setPropertyValue(null);
    setAssociationValue(null);
    setAssociationDisplayName(null);
    setStageValue(null);
  }, []);

  const handlePropertyChange = useCallback((value: unknown) => {
    setPropertyValue(value);
  }, []);

  // CrmFieldValueInput's association onChange emits string | string[] | null;
  // a pipeline stage is single-valued, so collapse to the chosen entity_id.
  const handleStageChange = useCallback((value: string | string[] | null) => {
    if (typeof value === 'string') {
      setStageValue(value);
    } else if (Array.isArray(value)) {
      setStageValue(value[0] ?? null);
    } else {
      setStageValue(null);
    }
  }, []);

  const handleAssociationChange = useCallback(
    (value: string | string[] | null) => {
      if (value === null) {
        setAssociationValue(null);
      } else {
        setAssociationValue(Array.isArray(value) ? value : [value]);
      }
    },
    []
  );

  const handleAssociationObjectsChange = useCallback(
    (objects: Record<string, CrmObject>) => {
      const firstObj = Object.values(objects)[0];
      setAssociationDisplayName(firstObj ? getDisplayName(firstObj) : null);
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (!selectedField) {
      return;
    }
    if (selectedField.kind === 'property') {
      const isMulti = selectedField.schema.is_multi;
      void submit({
        kind: 'property',
        propertyTypeName: selectedField.name,
        value: isMulti ? (propertyValue ?? []) : propertyValue,
        messages: {
          successTitle: 'Field updated',
          successDescription: `${displayCount} ${entityLabel} updated successfully`,
          incomplete: `Update incomplete. Some ${entityLabel} may not have been updated. Try again.`,
        },
      });
      return;
    }
    if (selectedField.kind === 'association') {
      let targetEntityId: string | null;
      if (isSelectedFieldStageAssoc) {
        targetEntityId = stageValue;
      } else {
        targetEntityId =
          Array.isArray(associationValue) && associationValue.length > 0
            ? associationValue[0]
            : null;
      }
      if (!targetEntityId) {
        return;
      }
      void submit({
        kind: 'association',
        associationTypeName: selectedField.name,
        targetEntityId,
        messages: {
          successTitle: 'Field updated',
          successDescription: `${displayCount} ${entityLabel} updated successfully`,
          incomplete: `Update incomplete. Some ${entityLabel} may not have been updated. Try again.`,
        },
      });
    }
  }, [
    selectedField,
    isSelectedFieldStageAssoc,
    stageValue,
    associationValue,
    propertyValue,
    submit,
    displayCount,
    entityLabel,
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  const footer = useMemo(
    () => (
      <div className='flex items-center justify-end gap-3'>
        <Button
          color='secondary'
          size='md'
          onClick={onClose}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color='primary'
          size='md'
          onClick={handleConfirm}
          isDisabled={!canConfirm}
          isLoading={isSubmitting}
        >
          Confirm
        </Button>
      </div>
    ),
    [onClose, isSubmitting, handleConfirm, canConfirm]
  );

  const fieldPicker = useMemo(() => {
    if (isLoading) {
      return (
        <div className='flex items-center gap-2 py-4 text-sm text-fg-secondary'>
          <LoadingIndicator type='line-simple' size='sm' />
          Loading fields…
        </div>
      );
    }

    if (fields.length === 0) {
      return (
        <p className='py-4 text-sm text-fg-secondary'>
          No fields can be bulk-updated for this type.
        </p>
      );
    }

    return (
      <div className='space-y-1.5'>
        <p className='text-sm text-fg-secondary'>
          Choose a field to update for {displayCount} {entityLabel}:
        </p>
        <Select
          aria-label='Field to update'
          placeholder='Select a field'
          size='sm'
          selectedKey={selectedFieldKey}
          onSelectionChange={handleFieldKeyChange}
          isDisabled={isSubmitting}
        >
          {stageOptions.length > 0 && (
            <AriaSection id='stage'>
              <AriaHeader className={FIELD_GROUP_HEADER_CLASS}>
                Pipeline
              </AriaHeader>
              {stageOptions.map(option => (
                <Select.Item key={option.key} id={option.key}>
                  {option.label}
                </Select.Item>
              ))}
            </AriaSection>
          )}
          {propertyOptions.length > 0 && (
            <AriaSection id='properties'>
              <AriaHeader className={FIELD_GROUP_HEADER_CLASS}>
                Properties
              </AriaHeader>
              {propertyOptions.map(option => (
                <Select.Item key={option.key} id={option.key}>
                  {option.label}
                </Select.Item>
              ))}
            </AriaSection>
          )}
          {associationOptions.length > 0 && (
            <AriaSection id='associations'>
              <AriaHeader className={FIELD_GROUP_HEADER_CLASS}>
                Associations
              </AriaHeader>
              {associationOptions.map(option => (
                <Select.Item key={option.key} id={option.key}>
                  {option.label}
                </Select.Item>
              ))}
            </AriaSection>
          )}
        </Select>
      </div>
    );
  }, [
    isLoading,
    fields.length,
    displayCount,
    entityLabel,
    selectedFieldKey,
    handleFieldKeyChange,
    isSubmitting,
    stageOptions,
    propertyOptions,
    associationOptions,
  ]);

  const valueInputContent = useMemo(() => {
    if (!selectedField) {
      return null;
    }

    if (selectedField.kind === 'property') {
      return (
        <div className='space-y-3'>
          {isSelectedFieldMultiProperty && (
            <p className='text-xs text-fg-tertiary'>
              This will replace the existing list for all selected {entityLabel}
              .
            </p>
          )}
          <CrmFieldValueInput
            kind='property'
            schema={selectedField.schema}
            value={propertyValue as string | number | object | null | undefined}
            onChange={handlePropertyChange}
            disabled={isSubmitting}
          />
        </div>
      );
    }

    // Association. Both the entity picker and the grouped pipeline-stage picker
    // are rendered by CrmFieldValueInput (routed via stageTypeNames) — the same
    // shared inputs the slideout overview uses, so stages group by pipeline
    // instead of showing a flat, duplicated list.
    if (!selectedAssociationType) {
      return (
        <div className='flex items-center gap-2 py-4 text-sm text-fg-secondary'>
          <LoadingIndicator type='line-simple' size='sm' />
          Loading…
        </div>
      );
    }

    if (isSelectedFieldStageAssoc) {
      return (
        <div className='space-y-3'>
          <CrmFieldValueInput
            kind='association'
            associationType={selectedAssociationType}
            value={stageValue}
            onChange={handleStageChange}
            orgId={orgId}
            stageTypeNames={stageTypeNames}
            disabled={isSubmitting}
            disableAutoSelect
          />
        </div>
      );
    }

    return (
      <div className='space-y-3'>
        <CrmFieldValueInput
          kind='association'
          associationType={selectedAssociationType}
          value={associationValue}
          onChange={handleAssociationChange}
          orgId={orgId}
          stageTypeNames={stageTypeNames}
          disabled={isSubmitting}
          onSelectedObjectsChange={handleAssociationObjectsChange}
        />
      </div>
    );
  }, [
    selectedField,
    entityLabel,
    isSelectedFieldMultiProperty,
    propertyValue,
    handlePropertyChange,
    isSubmitting,
    isSelectedFieldStageAssoc,
    stageValue,
    handleStageChange,
    stageTypeNames,
    selectedAssociationType,
    associationValue,
    handleAssociationChange,
    orgId,
    handleAssociationObjectsChange,
  ]);

  const confirmSummary = useMemo(() => {
    if (!selectedField || !canConfirm) {
      return null;
    }
    return (
      <div className='rounded-lg bg-bg-secondary px-3 py-2 text-sm text-fg-secondary'>
        <span>
          Update <strong>{selectedField.label}</strong>
          {valueDisplay != null ? (
            <>
              {' '}
              to <strong>{valueDisplay}</strong>
            </>
          ) : null}{' '}
          for {displayCount} {entityLabel}?
        </span>
        {isSelectedFieldMultiProperty && (
          <p className='mt-0.5 text-xs text-fg-tertiary'>
            Replaces the existing list for all selected {entityLabel}.
          </p>
        )}
      </div>
    );
  }, [
    selectedField,
    canConfirm,
    valueDisplay,
    displayCount,
    entityLabel,
    isSelectedFieldMultiProperty,
  ]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalOverlay isDismissable={!isSubmitting}>
        <Modal className='sm:max-w-md'>
          <Dialog
            title='Bulk update'
            instance='bulk-update'
            isCloseDisabled={isSubmitting}
            footer={footer}
          >
            <div className='space-y-4 pb-2'>
              {fieldPicker}
              {selectedField && valueInputContent}
              {selectedField && confirmSummary}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}
